export interface BrowseFilters {
  city: string;
  categories: string[];
  transmission: string;
  maxPrice: number;
  instantBook: boolean;
  minSeats: number | null;
}

interface FilterSidebarProps {
  filters: BrowseFilters;
  cities: string[];
  categories: string[];
  onChange: (patch: Partial<BrowseFilters>) => void;
  onReset: () => void;
}

export default function FilterSidebar({ filters, cities, categories, onChange, onReset }: FilterSidebarProps) {
  const toggleCategory = (category: string) => {
    const next = filters.categories.includes(category)
      ? filters.categories.filter((item) => item !== category)
      : [...filters.categories, category];
    onChange({ categories: next });
  };

  return (
    <div className="space-y-6 rounded-2xl surface-card p-5">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-base font-semibold text-foreground-950">Filters</h3>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-primary-500 transition-colors hover:text-primary-400"
        >
          <i className="ri-refresh-line"></i> Reset
        </button>
      </div>

      <div>
        <label htmlFor="filter-city" className="mb-2 block text-xs font-medium uppercase tracking-wider text-foreground-500">
          City
        </label>
        <div className="relative">
          <select
            id="filter-city"
            value={filters.city}
            onChange={(event) => onChange({ city: event.target.value })}
            className="w-full cursor-pointer appearance-none rounded-lg border border-background-200/70 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 focus:border-primary-500 focus:outline-none"
          >
            <option value="">All cities</option>
            {cities.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
          <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500"></i>
        </div>
      </div>

      <div>
        <span className="mb-2 block text-xs font-medium uppercase tracking-wider text-foreground-500">Category</span>
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => {
            const active = filters.categories.includes(category);
            return (
              <button
                key={category}
                type="button"
                onClick={() => toggleCategory(category)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-200 bg-background-50 text-foreground-700 hover:border-primary-500/50"
                }`}
              >
                {category}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <span className="mb-2 block text-xs font-medium uppercase tracking-wider text-foreground-500">Transmission</span>
        <div className="flex gap-2">
          {["Any", "Automatic", "Manual"].map((option) => {
            const value = option === "Any" ? "" : option;
            const active = filters.transmission === value;
            return (
              <button
                key={option}
                type="button"
                onClick={() => onChange({ transmission: value })}
                className={`flex-1 rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
                  active
                    ? "bg-accent-500 text-background-50"
                    : "border border-background-200 bg-background-50 text-foreground-700 hover:border-accent-500/50"
                }`}
              >
                {option}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <label htmlFor="filter-price" className="text-xs font-medium uppercase tracking-wider text-foreground-500">
            Max price / day
          </label>
          <span className="text-xs font-medium text-foreground-900">KSh {filters.maxPrice.toLocaleString("en-KE")}</span>
        </div>
        <input
          id="filter-price"
          type="range"
          min={3000}
          max={30000}
          step={500}
          value={filters.maxPrice}
          onChange={(event) => onChange({ maxPrice: Number(event.target.value) })}
          className="w-full cursor-pointer accent-primary-500"
        />
      </div>

      <div>
        <span className="mb-2 block text-xs font-medium uppercase tracking-wider text-foreground-500">Seats</span>
        <div className="flex flex-wrap gap-2">
          {[null, 5, 7, 14].map((option) => {
            const active = filters.minSeats === option;
            return (
              <button
                key={String(option)}
                type="button"
                onClick={() => onChange({ minSeats: option })}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  active
                    ? "bg-secondary-500 text-background-50"
                    : "border border-background-200 bg-background-50 text-foreground-700 hover:border-secondary-500/50"
                }`}
              >
                {option === null ? "Any" : `${option}+`}
              </button>
            );
          })}
        </div>
      </div>

      <label className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-3 py-2.5">
        <span className="flex items-center gap-2 text-sm text-foreground-800">
          <i className="ri-flashlight-line text-primary-500"></i> Instant book only
        </span>
        <input
          type="checkbox"
          checked={filters.instantBook}
          onChange={(event) => onChange({ instantBook: event.target.checked })}
          className="h-4 w-4 cursor-pointer accent-primary-500"
        />
      </label>
    </div>
  );
}